/**
 * SOLID Review
 * S — Strong. Sole job: browser-storage reads and writes for the two identity-adjacent records — the
 *     account's JWT (read, save, clear, and the player a valid one belongs to) and the stored game
 *     (`meyer_resume`: get/save/clear). Each keeps its one-time legacy-storage migration.
 * O — Good. A third persisted key would be a new group of functions here, not a change to the transports.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two small, independent helper groups — a consumer needing only one doesn't reason about the other.
 * D — Strong. Depends only on tabPersistentStorage — no Apollo/GraphQL imports at all.
 *
 * MOVED FROM shared/ (frontend strict-ports plan, FE2, 2026-10-03): every consumer is Infrastructure. FE3
 * moved the JWT here from the auth store, so it is held in ONE place and never leaves Infrastructure:
 * `ClientAuthServer` saves it when a code is accepted and clears it on sign-out, and the GraphQL
 * transports, the legal and consent clients and the payment calls all read it through
 * `getSessionToken`.
 *
 * FIXED IN FE3 (§28.199's finding): the transports read `meyer_session_token`, which nothing had written
 * since the auth store began keeping the JWT under `meyerlive3d_jwt` — so every GraphQL request went out
 * without `Authorization`, and the backend (which derives the caller from it, §28.75) recorded no media
 * presence for anyone. The retired key is gone; there is one key now.
 */
import { tabPersistentStorage } from '../../shared/tabPersistentStorage';

const JWT_STORAGE_KEY = 'meyerlive3d_jwt';
const RESUME_SESSION_KEY = 'meyer_resume';

// ---------------------------------------------------------------------------
// The account's JWT — kept PER-TAB but DISCARD-DURABLE via tabPersistentStorage (window.name tab id +
// namespaced localStorage). This keeps the §13.11 per-tab account isolation (two tabs can be two
// different accounts) while surviving a mobile OS tab discard, which wipes sessionStorage and used to
// sign the player out on return. Legacy sessionStorage / shared-localStorage copies migrate on first read.
// ---------------------------------------------------------------------------

/** The stored JWT, as stored — whether it is still valid is the server's call, or `readSessionAccount`'s. */
export function getSessionToken(): string | null {
  try {
    const jwt = tabPersistentStorage.getItem(JWT_STORAGE_KEY);
    if (jwt) return jwt;
    // One-time migration from earlier implementations (sessionStorage, then the original shared
    // localStorage) so existing logged-in users aren't logged out.
    const legacyJwt = sessionStorage.getItem(JWT_STORAGE_KEY) ?? localStorage.getItem(JWT_STORAGE_KEY);
    if (legacyJwt) {
      tabPersistentStorage.setItem(JWT_STORAGE_KEY, legacyJwt);
      sessionStorage.removeItem(JWT_STORAGE_KEY);
      localStorage.removeItem(JWT_STORAGE_KEY);
      return legacyJwt;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveSessionToken(jwt: string): void {
  try {
    tabPersistentStorage.setItem(JWT_STORAGE_KEY, jwt);
    sessionStorage.removeItem(JWT_STORAGE_KEY); // drop legacy per-tab copy
    localStorage.removeItem(JWT_STORAGE_KEY);   // drop legacy shared copy
  } catch {
    // storage unavailable (private mode): the token lives for this call only
  }
}

export function clearSessionToken(): void {
  try {
    tabPersistentStorage.removeItem(JWT_STORAGE_KEY);
    sessionStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(JWT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** The token's claims, or null when it is not three parts of base64url JSON. */
function claimsOf(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const claims: unknown = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    return claims !== null && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * The player a stored token belongs to, while it is valid (the auth store's `hydrate`, moved): none, an
 * unreadable token and an expired one read as null; a token without a subject is forgotten. A token with
 * no `exp` claim does not expire.
 */
export function readSessionAccount(): { playerId: string } | null {
  const jwt = getSessionToken();
  if (!jwt) return null;
  const claims = claimsOf(jwt);
  if (claims === null) return null;
  const exp = claims['exp'];
  if (typeof exp === 'number' && exp < Math.floor(Date.now() / 1000)) return null;
  const sub = claims['sub'];
  if (typeof sub !== 'string' || sub.length === 0) {
    clearSessionToken();
    return null;
  }
  return { playerId: sub };
}

// ---------------------------------------------------------------------------
// Resume session helpers — persisted across page refreshes
// ---------------------------------------------------------------------------

export interface IEntityResume {
  playerId: string;
  roomId: string;
  roomCode: string;
}

// Resume session is kept PER-TAB but DISCARD-DURABLE via tabPersistentStorage
// (window.name id + namespaced localStorage), mirroring JWT/guest identity. This
// stays per-tab (a refresh in one tab won't pick up another tab's resume target)
// while surviving an OS tab discard, so a backgrounded guest game can still be
// resumed on return. Legacy sessionStorage / shared-localStorage copies migrate on read.
export function getResumeSession(): IEntityResume | null {
  try {
    let raw = tabPersistentStorage.getItem(RESUME_SESSION_KEY);
    if (!raw) {
      // One-time migration from the previous sessionStorage / shared-localStorage copies.
      const legacy = sessionStorage.getItem(RESUME_SESSION_KEY) ?? localStorage.getItem(RESUME_SESSION_KEY);
      if (legacy) {
        tabPersistentStorage.setItem(RESUME_SESSION_KEY, legacy);
        sessionStorage.removeItem(RESUME_SESSION_KEY);
        localStorage.removeItem(RESUME_SESSION_KEY);
        raw = legacy;
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<IEntityResume>;
    if (parsed.playerId && parsed.roomId && parsed.roomCode) {
      return parsed as IEntityResume;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveResumeSession(session: IEntityResume): void {
  try {
    tabPersistentStorage.setItem(RESUME_SESSION_KEY, JSON.stringify(session));
    sessionStorage.removeItem(RESUME_SESSION_KEY);
    localStorage.removeItem(RESUME_SESSION_KEY);
  } catch {}
}

export function clearResumeSession(): void {
  try {
    tabPersistentStorage.removeItem(RESUME_SESSION_KEY);
    sessionStorage.removeItem(RESUME_SESSION_KEY);
    localStorage.removeItem(RESUME_SESSION_KEY);
  } catch {}
}
